/**
 * Public Trial Signup — /subscribe?code=<token>
 *
 * Step 1 of the two-link self-serve subscription flow. Anyone holding a
 * valid signed code (issued via `python manage.py issue_public_trial_link`)
 * can land on this page and create an account without an admin invite.
 *
 * On success the freshly issued JWT pair is persisted by `publicTrialService`
 * so the prospect is treated as logged-in. They are sent to /checkout to
 * complete payment (Step 2), where Stripe takes over with its own hosted
 * form — Aviatto never touches card data.
 *
 * Industry design system ("Assinar Trial" artboard): light ground, Z plate
 * + wordmark on top, a 2-column grid — main blueprint form card (1.25fr)
 * with uppercase mini-labels over square hairline inputs, and a right rail
 * of blueprint cards ("O trial inclui" checklist, "Primeiros 14 dias"
 * timeline, disclaimer). Status/validation states render as grammar boxes
 * (ink border for blocking, accent tint for positive) — no pills, no
 * gradients, no traffic-light password meter (grammar rule checklist).
 */

import { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { FlightTakeoff as FlightTakeoffIcon, LockOutlined as LockIcon } from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import authService from '../services/auth';
import publicTrialService from '../services/publicTrial';
import SubscriptionStepper from '../components/billing/SubscriptionStepper';

const initialForm = {
  full_name: '',
  email: '',
  username: '',
  password: '',
  password_confirm: '',
  company_name: '',
  phone_number: '',
  aircraft_prefix: '',
};

const REQUIRED_FIELDS = ['full_name', 'email', 'username', 'password', 'company_name'];

const PLAN_SUMMARY = {
  name: 'Aviatto Pro · Mensal',
  blurb: '1 operação · até 5 aeronaves · suporte 24/7',
  price: 'R$ 500',
  cadence: '/mês',
};

const TRIAL_INCLUDED = [
  'Checklist operacional e eFRAT (versões A e B) sem limite de trechos',
  'eDB, registro de voo e conciliação de combustível',
  'Safety Center com relatos, riscos e debrief',
  'Financeiro com custos de voo e contas a pagar',
  'WhatsApp para até 3 pilotos',
];

const TRIAL_TIMELINE = [
  { when: 'Dia 1', t: 'Cadastro da aeronave, tripulação e do próximo trecho.' },
  { when: 'Dia 2', t: 'Primeiro despacho completo: checklist, eFRAT e slot.' },
  { when: 'Dia 5', t: 'eDB e custo do voo fechados com conciliação de combustível.' },
  { when: 'Dia 14', t: 'Leitura de adesão, risco da frota e custo por hora da operação.' },
];

const HEADING = 'var(--font-heading)';
const INK = 'var(--color-neutral-900)';
const ACC = 'var(--color-accent)';
const ACC7 = 'var(--color-accent-700)';
const ACC8 = 'var(--color-accent-800)';
const MUTED55 = 'color-mix(in srgb, var(--color-text) 55%, transparent)';
const MUTED58 = 'color-mix(in srgb, var(--color-text) 58%, transparent)';
const MUTED62 = 'color-mix(in srgb, var(--color-text) 62%, transparent)';
const MUTED72 = 'color-mix(in srgb, var(--color-text) 72%, transparent)';

const labelSx = {
  display: 'block',
  fontFamily: HEADING,
  fontSize: '9.5px',
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: MUTED58,
  mb: '4px',
};

const inputStyle = {
  width: '100%',
  minHeight: 38,
  fontSize: 14,
  background: 'transparent',
  border: '1px solid var(--color-divider)',
  borderRadius: 0,
  padding: '0 11px',
  boxSizing: 'border-box',
  fontFamily: 'var(--font-body)',
  color: 'var(--color-text)',
};

const hintSx = {
  fontFamily: HEADING,
  fontSize: '9px',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'color-mix(in srgb, var(--color-text) 52%, transparent)',
  mt: '4px',
};

const errorBoxSx = {
  mt: '14px',
  p: '9px 11px',
  border: `1px solid ${INK}`,
  fontSize: 12.5,
  lineHeight: 1.45,
  color: INK,
};

const primaryBtnSx = {
  minHeight: 40,
  px: '18px',
  border: `1px solid ${ACC}`,
  background: ACC,
  color: 'var(--color-bg)',
  fontFamily: HEADING,
  fontSize: 12.5,
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  borderRadius: 0,
  cursor: 'pointer',
  '&:hover': { background: 'var(--color-accent-600)', borderColor: 'var(--color-accent-600)' },
  '&:active': { background: ACC7 },
  '&:disabled': { opacity: 0.45, cursor: 'default' },
};

const upLinkSx = {
  fontFamily: HEADING,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: ACC7,
  textDecoration: 'none',
  '&:hover': { color: ACC8 },
};

function ZPlateBrand() {
  return (
    <Box
      component="a"
      href="/login"
      sx={{ display: 'flex', alignItems: 'center', gap: '9px', textDecoration: 'none', color: 'var(--color-text)' }}
    >
      <Box component="span" sx={{ display: 'grid', placeItems: 'center', width: 30, height: 30, border: '1.5px solid var(--color-text)', fontFamily: HEADING, fontSize: 15, fontWeight: 600 }}>
        Z
      </Box>
      <Box component="span" sx={{ fontFamily: HEADING, fontSize: 19, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        Zulbot
      </Box>
    </Box>
  );
}

/**
 * Grammar rule checklist replacing the old traffic-light strength scale.
 * Square 15px plates: accent fill + check when the rule is met, hairline
 * border + muted otherwise (per the "Recuperar Senha" artboard).
 */
function buildPasswordRules(password, confirm) {
  return [
    { t: 'Mínimo de 8 caracteres', ok: password.length >= 8 },
    { t: 'Letra maiúscula e minúscula', ok: /[a-z]/.test(password) && /[A-Z]/.test(password) },
    { t: 'Número ou símbolo', ok: /[0-9\W]/.test(password) },
    { t: 'Confirmação igual', ok: Boolean(password) && password === confirm },
  ];
}

function RuleChecklist({ rules }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {rules.map((r) => (
        <Box key={r.t} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              width: 15,
              height: 15,
              fontFamily: HEADING,
              fontSize: 9,
              fontWeight: 600,
              border: `1px solid ${r.ok ? ACC : 'var(--color-divider)'}`,
              background: r.ok ? ACC : 'transparent',
              color: r.ok ? 'var(--color-bg)' : MUTED58,
            }}
          >
            {r.ok ? '✓' : ''}
          </Box>
          <Box
            component="span"
            sx={{
              fontFamily: HEADING,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: r.ok ? ACC7 : MUTED58,
            }}
          >
            {r.t}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function buildRedirectUrl(code, fallback = '/checkout') {
  const safeCode = encodeURIComponent(String(code || '').trim());
  if (!safeCode) return fallback;
  return `${fallback}?code=${safeCode}`;
}

export default function PublicTrialSignup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const code = (params.get('code') || '').trim();

  const [validationState, setValidationState] = useState({
    status: code ? 'loading' : 'missing',
    daysRemaining: null,
    expiresAt: null,
    error: '',
  });
  const [form, setForm] = useState(initialForm);
  // Pre-accepted by default so the flow isn't gated by a legal click — the
  // checkbox remains interactive so the prospect can opt out if they want
  // to review the Terms before Stripe takes over.
  const [acceptedTerms, setAcceptedTerms] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [fieldError, setFieldError] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!code) {
      setValidationState((prev) => ({ ...prev, status: 'missing' }));
      return () => {
        cancelled = true;
      };
    }

    setValidationState((prev) => ({ ...prev, status: 'loading', error: '' }));
    publicTrialService
      .validate(code)
      .then((data) => {
        if (cancelled) return;
        if (data?.valid) {
          setValidationState({
            status: 'valid',
            daysRemaining: data?.days_remaining ?? null,
            expiresAt: data?.expires_at || null,
            error: '',
          });
        } else {
          setValidationState({
            status: 'invalid',
            daysRemaining: null,
            expiresAt: null,
            error:
              'Este link de assinatura expirou ou nao e valido. Solicite um novo link a equipe Aviato.',
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setValidationState({
          status: 'error',
          daysRemaining: null,
          expiresAt: null,
          error: 'Nao foi possivel validar o link agora. Tente novamente em instantes.',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [code]);

  // Pre-populate username from email so the prospect doesn't need to invent one.
  useEffect(() => {
    if (!form.email || form.username) return;
    const localPart = form.email.split('@')[0] || '';
    if (localPart) {
      setForm((prev) => ({ ...prev, username: localPart.toLowerCase() }));
    }
    // Intentionally one-shot: only fill while username is still empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.email]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'aircraft_prefix' ? value.toUpperCase() : value,
    }));
    setSubmitError('');
    setFieldError('');
  };

  const isFormReady = useMemo(() => {
    return REQUIRED_FIELDS.every((key) => String(form[key] || '').trim().length > 0);
  }, [form]);

  const passwordRules = useMemo(
    () => buildPasswordRules(form.password, form.password_confirm),
    [form.password, form.password_confirm]
  );
  const passwordsMatch =
    form.password_confirm.length === 0 || form.password === form.password_confirm;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitError('');
    setFieldError('');

    if (!isFormReady) {
      setSubmitError('Preencha todos os campos obrigatorios antes de continuar.');
      return;
    }
    if (form.password.length < 8) {
      setFieldError('password');
      setSubmitError('A senha precisa ter pelo menos 8 caracteres.');
      return;
    }
    if (form.password !== form.password_confirm) {
      setFieldError('password_confirm');
      setSubmitError('As senhas nao coincidem.');
      return;
    }

    setSubmitting(true);
    try {
      authService.logout();
      await publicTrialService.register({
        code,
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        username: form.username.trim(),
        password: form.password,
        company_name: form.company_name.trim(),
        phone_number: form.phone_number.trim(),
        aircraft_prefix: form.aircraft_prefix.trim(),
      });
      navigate(buildRedirectUrl(code, '/checkout'), { replace: true });
    } catch (err) {
      const data = err?.response?.data || {};
      const message =
        data.error ||
        data.detail ||
        'Nao foi possivel criar sua conta. Tente novamente.';
      setFieldError(data.field || '');
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (validationState.status === 'loading') {
    return (
      <Box
        className="zb-industry"
        sx={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '14px',
          background: 'var(--color-bg)',
          color: 'var(--color-text)',
          fontFamily: 'var(--font-body)',
        }}
      >
        <ZPlateBrand />
        <Typography sx={{ fontFamily: HEADING, fontSize: '10px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: MUTED58 }}>
          Validando seu link de assinatura...
        </Typography>
      </Box>
    );
  }

  const linkBlocked =
    validationState.status === 'missing' ||
    validationState.status === 'invalid' ||
    validationState.status === 'error';

  const submitDisabled =
    linkBlocked || submitting || !isFormReady || !acceptedTerms || !passwordsMatch;

  const renderField = ({
    id,
    label,
    name,
    type = 'text',
    autoComplete,
    placeholder,
    hint,
    span,
    upper = false,
  }) => (
    <Box sx={{ gridColumn: { xs: 'auto', sm: span || 'auto' }, minWidth: 0 }}>
      <Box component="label" htmlFor={id} sx={labelSx}>
        {label}
      </Box>
      <input
        id={id}
        className="ind-input"
        name={name}
        type={type}
        value={form[name]}
        onChange={handleChange}
        autoComplete={autoComplete}
        placeholder={placeholder}
        disabled={linkBlocked || submitting}
        style={{
          ...inputStyle,
          textTransform: upper ? 'uppercase' : 'none',
          ...(upper ? { fontFamily: HEADING, fontWeight: 600, letterSpacing: '0.06em' } : {}),
          ...(fieldError === name ? { border: `1px solid ${INK}` } : {}),
        }}
      />
      {hint ? <Box sx={hintSx}>{hint}</Box> : null}
    </Box>
  );

  return (
    <Box
      className="zb-industry"
      sx={{
        minHeight: '100dvh',
        background: 'var(--color-bg)',
        color: 'var(--color-text)',
        fontFamily: 'var(--font-body)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: '32px 24px 44px',
        boxSizing: 'border-box',
      }}
    >
      <ZPlateBrand />

      <Box
        sx={{
          width: '100%',
          maxWidth: 980,
          mt: 3,
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.25fr) minmax(0, 1fr)' },
          gap: '14px',
          alignItems: 'start',
        }}
      >
        {/* ── Main blueprint form card ── */}
        <Box className="blueprint" sx={{ p: 3, background: 'transparent', boxShadow: 'var(--ind-shadow-md)', minWidth: 0 }}>
          <i className="corner tl" /><i className="corner tr" /><i className="corner bl" /><i className="corner br" />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <Box component="span" sx={{ fontFamily: HEADING, fontSize: 10, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: ACC7 }}>
              Nova conta · Ops
            </Box>
            <Box sx={{ flex: 1 }} />
            {validationState.status === 'valid' && validationState.daysRemaining !== null ? (
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '7px',
                  p: '3px 9px',
                  border: `1px solid ${ACC}`,
                  background: 'color-mix(in srgb, var(--color-accent) 8%, transparent)',
                  fontFamily: HEADING,
                  fontSize: '10px',
                  fontWeight: 600,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: ACC8,
                }}
              >
                <Box component="span" sx={{ width: 5, height: 5, background: ACC }} />
                LINK VALIDO — {validationState.daysRemaining} DIA(S)
              </Box>
            ) : null}
          </Box>

          <Typography
            component="h1"
            sx={{ m: '8px 0 0', fontFamily: HEADING, fontSize: 30, fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.02em' }}
          >
            Crie sua conta Aviatto
          </Typography>
          <Typography sx={{ m: '9px 0 0', fontSize: 13.5, lineHeight: 1.55, color: MUTED72, maxWidth: '60ch' }}>
            Passo 1 de 2 — Cadastre seu acesso. Depois disso, voce recebera o link de
            pagamento (Stripe) para iniciar a assinatura.
          </Typography>

          <SubscriptionStepper activeIndex={0} sx={{ mt: '18px' }} />

          {validationState.status === 'missing' ? (
            <Box sx={errorBoxSx}>
              Esta pagina precisa de um codigo de convite no link
              (ex.: <code style={{ fontFamily: 'inherit', fontWeight: 600 }}>?code=...</code>). Solicite o link de assinatura a equipe Aviato.
            </Box>
          ) : null}

          {validationState.status === 'invalid' || validationState.status === 'error' ? (
            <Box sx={errorBoxSx}>{validationState.error}</Box>
          ) : null}

          {submitError ? <Box sx={errorBoxSx}>{submitError}</Box> : null}

          {/* Plan summary — hairline row with ink icon plate */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '13px',
              p: '12px 14px',
              mt: '16px',
              border: '1px solid var(--color-divider)',
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ flexShrink: 0, display: 'grid', placeItems: 'center', width: 26, height: 24, background: INK, color: 'var(--color-bg)' }}>
              <FlightTakeoffIcon sx={{ fontSize: 15 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.2 }}>
                {PLAN_SUMMARY.name}
              </Typography>
              <Typography sx={{ fontFamily: HEADING, fontSize: '9.5px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: MUTED58, mt: '3px' }}>
                {PLAN_SUMMARY.blurb}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontFamily: HEADING, fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em' }}>
                {PLAN_SUMMARY.price}
                <Box component="span" sx={{ fontSize: 12, color: MUTED58, fontWeight: 600, ml: '3px' }}>
                  {PLAN_SUMMARY.cadence}
                </Box>
              </Typography>
              <Typography sx={{ fontFamily: HEADING, fontSize: '9.5px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: ACC7, mt: '2px' }}>
                14 dias grátis aplicados
              </Typography>
            </Box>
          </Box>

          <form onSubmit={handleSubmit} noValidate>
            <Box sx={{ fontFamily: HEADING, fontSize: '9.5px', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: MUTED58, mt: '20px' }}>
              CREDENCIAIS · ACESSO
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: '14px 18px',
                mt: '10px',
              }}
            >
              {renderField({
                id: 'trial-full-name',
                label: 'Nome completo',
                name: 'full_name',
                autoComplete: 'name',
                span: '1 / -1',
              })}
              {renderField({
                id: 'trial-email',
                label: 'E-mail',
                name: 'email',
                type: 'email',
                autoComplete: 'email',
                hint: 'Usado para notas fiscais e recuperação de acesso.',
                span: '1 / -1',
              })}
              {renderField({
                id: 'trial-username',
                label: 'Usuario para login',
                name: 'username',
                autoComplete: 'username',
                hint: 'Sera usado em conjunto com a senha para acessar o painel.',
              })}
              {renderField({
                id: 'trial-company',
                label: 'Empresa / operacao',
                name: 'company_name',
                autoComplete: 'organization',
              })}
              {renderField({
                id: 'trial-password',
                label: 'Senha',
                name: 'password',
                type: 'password',
                autoComplete: 'new-password',
              })}
              {renderField({
                id: 'trial-password-confirm',
                label: 'Confirme a senha',
                name: 'password_confirm',
                type: 'password',
                autoComplete: 'new-password',
              })}
              <Box sx={{ gridColumn: '1 / -1' }}>
                <RuleChecklist rules={passwordRules} />
                {!passwordsMatch ? (
                  <Box sx={{ fontFamily: HEADING, fontSize: '10px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: INK, mt: '6px' }}>
                    As senhas nao coincidem.
                  </Box>
                ) : null}
              </Box>
            </Box>

            {/* OPS metadata — dashed-top optional section */}
            <Box sx={{ mt: '18px', pt: '14px', borderTop: '1px dashed var(--color-neutral-300)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '10px', flexWrap: 'wrap' }}>
                <Box component="span" sx={{ fontFamily: HEADING, fontSize: '9.5px', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: MUTED58 }}>
                  OPS METADATA
                </Box>
                <Box sx={{ flex: 1 }} />
                <Box component="span" sx={{ fontFamily: HEADING, fontSize: '9px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'color-mix(in srgb, var(--color-text) 52%, transparent)' }}>
                  opcionais — pode adicionar depois
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
                  gap: '14px 18px',
                }}
              >
                {renderField({
                  id: 'trial-phone',
                  label: 'WhatsApp (opcional)',
                  name: 'phone_number',
                  autoComplete: 'tel',
                  placeholder: '+55 11 99999-9999',
                })}
                {renderField({
                  id: 'trial-prefix',
                  label: 'Prefixo da aeronave',
                  name: 'aircraft_prefix',
                  placeholder: 'PR-AVI',
                  upper: true,
                })}
              </Box>
            </Box>

            {/* Terms — square check plate row */}
            <Box
              component="button"
              type="button"
              role="checkbox"
              aria-checked={acceptedTerms}
              disabled={linkBlocked || submitting}
              onClick={() => setAcceptedTerms((v) => !v)}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                mt: '18px',
                background: 'transparent',
                border: 0,
                p: 0,
                cursor: 'pointer',
                textAlign: 'left',
                font: 'inherit',
                color: 'inherit',
                '&:disabled': { opacity: 0.45, cursor: 'default' },
              }}
            >
              <Box
                component="span"
                sx={{
                  flexShrink: 0,
                  display: 'grid',
                  placeItems: 'center',
                  width: 18,
                  height: 18,
                  mt: '1px',
                  fontFamily: HEADING,
                  fontSize: 11,
                  fontWeight: 600,
                  border: `1.5px solid ${acceptedTerms ? ACC : 'var(--color-divider)'}`,
                  background: acceptedTerms ? ACC : 'transparent',
                  color: acceptedTerms ? 'var(--color-bg)' : MUTED58,
                }}
              >
                {acceptedTerms ? '✓' : ''}
              </Box>
              <Box component="span" sx={{ flex: 1, minWidth: 0, fontSize: 12.5, lineHeight: 1.55, color: 'color-mix(in srgb, var(--color-text) 74%, transparent)' }}>
                Concordo com os{' '}
                <Box component="a" href="/terms" sx={{ color: ACC7, fontWeight: 600, textDecoration: 'none', '&:hover': { color: ACC8 } }}>
                  Termos de Uso
                </Box>{' '}
                e a{' '}
                <Box component="a" href="/privacy" sx={{ color: ACC7, fontWeight: 600, textDecoration: 'none', '&:hover': { color: ACC8 } }}>
                  Política de Privacidade
                </Box>
                . O pagamento é processado pela Stripe; a Aviatto não armazena dados de cartão.
              </Box>
            </Box>

            {/* Footer: trust note + primary CTA */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '11px',
                mt: '20px',
                pt: 2,
                borderTop: '1px dashed var(--color-neutral-300)',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', color: MUTED55 }}>
                <LockIcon sx={{ fontSize: 15 }} />
                <Box component="span" sx={{ fontFamily: HEADING, fontSize: '10px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                  SSL · 256-bit · handoff seguro
                </Box>
              </Box>
              <Box sx={{ flex: 1 }} />
              <Box component="button" type="submit" disabled={submitDisabled} sx={primaryBtnSx}>
                {submitting ? 'Criando conta...' : 'Criar conta e ir para pagamento'}
              </Box>
            </Box>
          </form>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              mt: '18px',
              pt: '15px',
              borderTop: '1px dashed var(--color-neutral-300)',
              flexWrap: 'wrap',
            }}
          >
            <Box component="span" sx={{ fontSize: 12.5, color: MUTED72 }}>
              Ja possui conta?
            </Box>
            <Box component={RouterLink} to="/login" sx={upLinkSx}>
              Entrar
            </Box>
          </Box>
        </Box>

        {/* ── Right rail: included / timeline / disclaimer ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 }}>
          <Box className="blueprint" sx={{ p: '16px 17px', background: 'transparent', boxShadow: 'var(--ind-shadow-sm)' }}>
            <i className="corner tl" /><i className="corner tr" /><i className="corner bl" /><i className="corner br" />
            <Box sx={{ fontFamily: HEADING, fontSize: 11, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: MUTED62 }}>
              O trial inclui
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', mt: '11px' }}>
              {TRIAL_INCLUDED.map((item) => (
                <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: '9px' }}>
                  <Box
                    component="span"
                    sx={{
                      flexShrink: 0,
                      display: 'grid',
                      placeItems: 'center',
                      width: 16,
                      height: 16,
                      mt: '1px',
                      fontFamily: HEADING,
                      fontSize: 9,
                      fontWeight: 600,
                      border: `1px solid ${ACC}`,
                      background: ACC,
                      color: 'var(--color-bg)',
                    }}
                  >
                    ✓
                  </Box>
                  <Box component="span" sx={{ flex: 1, minWidth: 0, fontSize: 12.5, lineHeight: 1.45 }}>
                    {item}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Box className="blueprint" sx={{ p: '16px 17px', background: 'transparent', boxShadow: 'var(--ind-shadow-sm)' }}>
            <i className="corner tl" /><i className="corner tr" /><i className="corner bl" /><i className="corner br" />
            <Box sx={{ fontFamily: HEADING, fontSize: 11, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: MUTED62 }}>
              Primeiros 14 dias
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mt: '11px' }}>
              {TRIAL_TIMELINE.map((t) => (
                <Box key={t.when} sx={{ borderLeft: `2px solid ${ACC}`, p: '2px 0 2px 11px' }}>
                  <Box sx={{ fontFamily: HEADING, fontSize: '9.5px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: ACC7 }}>
                    {t.when}
                  </Box>
                  <Box sx={{ fontSize: 12.5, lineHeight: 1.45, mt: '2px' }}>{t.t}</Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Box className="blueprint" sx={{ p: '16px 17px', background: 'transparent', boxShadow: 'var(--ind-shadow-sm)' }}>
            <i className="corner tl" /><i className="corner tr" /><i className="corner bl" /><i className="corner br" />
            <Box sx={{ fontSize: 11.5, lineHeight: 1.55, color: MUTED72 }}>
              Plataforma de registro e apoio à decisão: não opera aeronaves, não exerce controle
              operacional e não decide prosseguir ou não prosseguir. A decisão final é sempre do comandante.
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
